import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { REPORTS_META } from '../constants/MiniReportsMeta';
import appConfig from '../config/appConfig.json'
import UserDetailsInterface from './UserDetailsInterface';
import './ReportDetailsPage.css';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import ReactGA from 'react-ga4';
import { useNavigate } from 'react-router-dom';
import { useDebounce } from 'use-debounce';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  setDoc,
  serverTimestamp
} from 'firebase/firestore';
import axios from 'axios';
import { firestore } from '../firebase';

const ReportDetailPage = () => {
  const [searchParams] = useSearchParams();
  const reportType = searchParams.get('type');
  const userId = localStorage.getItem('userId');
  const [userData, setUserData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [sectionLoading, setSectionLoading] = useState(false);
  const [miniReportId, setMiniReportId] = useState(null);
  const [reportData, setReportData] = useState({});
  const [selectedSection, setSelectedSection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openSection, setOpenSection] = useState(meta?.sections?.[0] || null);
  const [loadingSection, setLoadingSection] = useState(null);
  const navigate = useNavigate();
  const [loadingPurchase, setLoadingPurchase] = useState(false);
  const [showPartnerChoicePopup, setShowPartnerChoicePopup] = useState(false);
  const [partnerPlaceInput, setPartnerPlaceInput] = useState('');
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [locationPredictions, setLocationPredictions] = useState([]);
  const [debouncedPlace] = useDebounce(partnerPlaceInput, 300);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationSelected, setLocationSelected] = useState(false);
  const [partnerDetails, setPartnerDetails] = useState(null);
  const [hasTypedPlace, setHasTypedPlace] = useState(false);
  const [prefilledPartnerDetails, setPrefilledPartnerDetails] = useState({
    name: localStorage.getItem('partnerName') || '',
    dob: localStorage.getItem('partnerDob') || '',
    time: localStorage.getItem('partnerTime') || '',
    place: localStorage.getItem('partnerPlace') || '',
  });

  useEffect(() => {
    setPartnerPlaceInput(prefilledPartnerDetails.place || '');
  }, []);  

  useEffect(() => {
    if (debouncedPlace && !locationSelected && hasTypedPlace) {
      fetchLocationPredictions(debouncedPlace);
    }
  }, [debouncedPlace, locationSelected, hasTypedPlace]);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!userId) return;
  
      try {
        const userRef = doc(firestore, 'users', userId);
        const userDoc = await getDoc(userRef);
  
        if (userDoc.exists()) {
          const data = userDoc.data();
          setUserData(data);
          const { resp } = await axios.post(`${appConfig.REACT_APP_BACKEND_URL}/mini-reports/verify-purchase`, {
            reportType,
            userId: userId
          });
          const {success, message, amount} = resp;
          if(success) {
            setIsUnlocked(true);
            setMiniReportId(miniReportId);
  
            // Step 7: Track success
            trackGA4Event('plan_purchase_success', {
              source: 'Mini Report',
              report_type: reportType,
              amount: 29
            });
          }
        }
      } catch (error) {
        console.error("Failed to fetch user data", error);
      }
    };
  
    fetchUserData();
  }, [userId]);        

  useEffect(() => {
    const loadReportMeta = async () => {
      if (!reportType || !userId) return;

      const reportMeta = REPORTS_META[reportType];
      if (!reportMeta) {
        navigate('/mini-reports');
        return;
      }
      setMeta(reportMeta);

      const q = query(
        collection(firestore, 'mini-reports'),
        where('userId', '==', userId),
        where('report_type', '==', reportType)
      );

      const snapshot = await getDocs(q);
      const docSnap = snapshot.docs[0];
      if (docSnap?.exists()) {
        const data = docSnap.data();
        console.log(data)
        if (data.payment_complete) {
          setIsUnlocked(true);
          setReportData(data.sections || {});
          console.log(docSnap.id)
          setMiniReportId(docSnap.id);
      
          // Immediately fetch if first section is missing
          if (!data.sections?.[reportMeta.sections[0]]) {
            fetchSectionContent(reportMeta.sections[0]);
          }
        }
      } else {
        if (reportType === 'love-compatibility') {
          setShowPartnerChoicePopup(true);
        }        
      }
      

      setLoading(false);
    };

    loadReportMeta();
  }, [reportType, userId]);

  const getPlatform = () => {
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) {
      return 'PWA';
    }
    return 'Website';
  };

  const trackGA4Event = (eventName, params = {}) => {
    ReactGA.event(eventName, {
      platform: getPlatform(), // optional, if you want
      ...params,
    });
  };

  const getCookieValue = (name) => {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.startsWith(`${name}=`)) {
        return cookie.substring(name.length + 1);
      }
    }
    return null;
  };

  const fetchLocationPredictions = async (input) => {
    setLocationLoading(true);
    try {
      const response = await axios.get(`${appConfig.REACT_APP_BACKEND_URL}/geocode/autocomplete`, {
        params: { text: input }
      });
      setLocationPredictions(response.data || []);
    } catch (error) {
      console.error('Error fetching location predictions:', error);
    } finally {
      setLocationLoading(false);
    }
  };

  const hasAnyDashaExpired = (dashaData) => {
    if (!dashaData || !dashaData.current_dasha) return true;
  
    const levels = [
      'major',
      'minor',
      'sub_minor',
      'sub_sub_minor',
      'sub_sub_sub_minor'
    ];
  
    const nowIST = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
  
    return levels.some(level => {
      const dasha = dashaData.current_dasha[level];
      if (!dasha || !dasha.end) return true;
  
      try {
        const [datePart, timePart] = dasha.end.trim().split(/\s+/);
        const [day, month, year] = datePart.split('-').map(Number);
        const [hour, minute] = timePart.split(':').map(Number);
  
        // Build UTC date from IST components, then shift to IST
        const dashaEndUTC = new Date(Date.UTC(year, month - 1, day, hour - 5, minute - 30));
        const dashaEndIST = new Date(dashaEndUTC.getTime() + 19800000); // 5.5 hours in ms
  
        return nowIST > dashaEndIST;
      } catch (e) {
        console.warn(`Failed to parse dasha end date for ${level}`, e);
        return true;
      }
    });
  };  

  const fetchPartnerDetails = async (partnerData) => {
    try {
      console.log(partnerData)
      // Send user data to the backend for astrology API calls and Firestore update
      const response = await axios.post(`${appConfig.REACT_APP_BACKEND_URL}/api/astrology-details`, partnerData);
  
      if (response.status === 200) {
        const astroData = response.data;
  
        // Update the frontend state with the received astroData
        let dashaData = null;
        const dashaResponse = await axios.post(`${appConfig.REACT_APP_BACKEND_URL}/dasha/current-dasha`, userData);
        if (dashaResponse.status === 200) {
          dashaData = dashaResponse.data;
        }
        localStorage.setItem('partnerAstroData', JSON.stringify(astroData));
        localStorage.setItem('partnerDashaData', JSON.stringify(dashaData));
      }
    } catch (error) {
      console.error("Error fetching astrology details:", error);
    }
  }

  const storePartnerDetails = (details) => {
    trackGA4Event('partner details added', {
      source: 'mini-report',
    });
    if (!details) {
      console.warn("No partner details found");
      return;
    }
  
    // Retrieve existing details from localStorage
    const existingDetails = {
      name: localStorage.getItem('partnerName') || '',
      dob: localStorage.getItem('partnerDob') || '',
      time: localStorage.getItem('partnerTime') || '',
      place: localStorage.getItem('partnerPlace') || '',
    };
  
    // Check if any detail has changed
    const hasChanged = Object.keys(existingDetails).some(
      (key) => String(existingDetails[key]) !== String(details[key])
    );
  
    // Save updated values
    localStorage.setItem('partnerName', details.name);
    localStorage.setItem('partnerDob', details.dob);
    localStorage.setItem('partnerTime', details.time);
    localStorage.setItem('partnerPlace', details.place);

    const partnerDashaDataRaw = localStorage.getItem('partnerDashaData');
    const partnerDashaData = partnerDashaDataRaw ? JSON.parse(partnerDashaDataRaw) : null;

    if (!partnerDashaData || hasChanged || hasAnyDashaExpired(partnerDashaData)) {
      console.log("Partner details changed or dasha expired, fetching new astrology data...");
      localStorage.setItem('partnerLat', details.lat);
      localStorage.setItem('partnerLon', details.lon);
      const enrichedDetails = {
        name: details.name,
        dob: details.dob,
        time: details.time,
        place: details.place,
        lat: details.lat,
        lon: details.lon
      };
    
      fetchPartnerDetails(enrichedDetails);
    }
  };

  const handleReportPurchase = async (reportType, price) => {
    try {
      // Step 1: GA tracking
      trackGA4Event('plan_purchase_initiated', {
        source: 'Mini Report',
        report_type: reportType,
        amount: price
      });
  
      // Step 2: Get tracking cookies
      const fbc = getCookieValue('_fbc') || null;
      const fbp = getCookieValue('_fbp') || null;
  
      // Step 3: Call backend to create Razorpay order
      const { data } = await axios.post(`${appConfig.REACT_APP_BACKEND_URL}/mini-reports/initiate-payment`, {
        reportType,
        amount: price,
        userId: userId
      });
  
      const { orderId, miniReportId } = data;
  
      // Step 4: Configure Razorpay options
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: price * 100,
        currency: 'INR',
        name: 'Vedic Rashi',
        description: `Mini Report - ${reportType}`,
        order_id: orderId,
        handler: async (response) => {
          try {
            // Step 5: Confirm payment with backend
            await axios.post(`${appConfig.REACT_APP_BACKEND_URL}/mini-reports/confirm-payment`, {
              miniReportId,
              razorpay_payment_id: response.razorpay_payment_id,
              fbp,
              fbc
            });
  
            // Step 6: Unlock on frontend
            setIsUnlocked(true);
            setMiniReportId(miniReportId);
  
            // Step 7: Track success
            trackGA4Event('plan_purchase_success', {
              source: 'Mini Report',
              report_type: reportType,
              amount: price
            });
  
          } catch (err) {
            console.error("Payment confirmation failed", err);
            const { data } = await axios.post(`${appConfig.REACT_APP_BACKEND_URL}/mini-reports/verify-purchase`, {
              reportType,
              userId: userId
            });
            const {success, message, amount} = data;
            if(success) {
              setIsUnlocked(true);
              setMiniReportId(miniReportId);
    
              // Step 7: Track success
              trackGA4Event('plan_purchase_success', {
                source: 'Mini Report',
                report_type: reportType,
                amount: price
              });
            } else {
              alert("Payment was successful but confirmation failed. Please contact support.");
            }
          }
        }
      };
  
      const paymentObject = new window.Razorpay(options);
      setLoadingPurchase(true);
      paymentObject.open();
      setLoadingPurchase(false);  
    } catch (error) {
      console.error("Purchase initiation failed:", error);
      alert("Could not start payment. Please try again.");
    }
  };

  const fetchSectionContent = async (sectionName) => {
    if (!sectionName || !meta || !userData) {
      return;
    }      
  
    if (reportData[sectionName]) {
      setOpenSection(sectionName); // Just open it
      return;
    }

    if (reportType === 'love-compatibility' && showPartnerChoicePopup) {
      return; // don't fetch unless partner details are entered
    }
  
    setLoadingSection(sectionName);
    setOpenSection(sectionName);
    setSectionLoading(true);
    let partnerData = null;
    if (reportType === 'love-compatibility' && isUnlocked) {
      partnerData = {
        astroData: JSON.parse(localStorage.getItem('partnerAstroData') || '{}'),
        dashaData: JSON.parse(localStorage.getItem('partnerDashaData') || '{}'),
        name: localStorage.getItem('partnerName') || ''
      };
    }
    if (!miniReportId) {
      window.location.reload();
      return; // optional: to prevent further execution
    }
    try {
      const { data } = await axios.post(`${appConfig.REACT_APP_BACKEND_URL}/mini-reports/get-mini-report-section`, {
        reportType,
        sectionName,
        userData,
        partnerData: partnerData,
        language: userData.language || 'hi',
        transactionId: `${userId}_${reportType}`,
        miniReportId: miniReportId
      });
  
      const content = data.content || 'No content available.';
  
      const updatedSections = {
        ...reportData,
        [sectionName]: content,
      };
  
      setReportData(updatedSections);
      setSelectedSection(sectionName);
  
    } catch (err) {
      console.error("Error loading section:", err?.response?.data || err.message);
      alert("Failed to load section. Try again.");
    }
  
    setSectionLoading(false);
  };
  
  if (!userData) {
    return <UserDetailsInterface />;
  }
  
  if (loading) {
    return (
      <div className="loadin-page">
        <div className="top-section">
          <div className="top-section-content">
            <Link to="/" className="no-underline">
              <i className="ri-arrow-left-line"></i>
            </Link>
            <div className="profile-pic-container">
              <img src="astro.png" alt="Astrologer" className="profile-pic" />
              <div className="status-dot"></div>
            </div>
            <div className="astrologer-info">
              <h4>Acharya Raghavendra</h4>
              <h5>Generating Report...</h5>
            </div>
          </div>
        </div>
        <div className="loading-page-container">
          <h3 className="loading-text">Looking at your stars...</h3>
          <div className="blur-balls"></div>
          <video
            className="loading-video"
            src="https://cdnl.iconscout.com/lottie/premium/thumb/magic-circle-animated-icon-download-in-lottie-json-gif-static-svg-file-formats--occult-magical-sun-mystic-sign-astrology-pack-entertainment-icons-8542368.mp4"
            autoPlay
            loop
            muted
          ></video>
        </div>
      </div>
    );
  }
  
  if (!meta) {
    return <p>Invalid report type.</p>;
  }
  if (showPartnerChoicePopup) {
    return (
      <div className="popup-overlay">
        <div className="popup-card">
          <h2>Enter your partner's details:</h2>
          <form onSubmit={(e) => {
            e.preventDefault();
            const form = e.target;
            const details = {
              name: form.name.value,
              dob: form.dob.value,
              time: form.time.value,
              place: partnerPlaceInput,
              lat: selectedLocation?.lat,
              lon: selectedLocation?.lon,
            };
            setPartnerDetails(details);
            setShowPartnerChoicePopup(false);
            storePartnerDetails(details);
          }}>
            <label>Partner's Name:</label>
            <input name="name" defaultValue={prefilledPartnerDetails.name} required />
  
            <label>Date of Birth:</label>
            <input name="dob" defaultValue={prefilledPartnerDetails.dob} type="date" required />
  
            <label>Time of Birth:</label>
            <input name="time" defaultValue={prefilledPartnerDetails.time} type="time" required />
  
            <label>Place of Birth:</label>
            <input
              type="text"
              name="place"
              defaultValue={prefilledPartnerDetails.place}
              value={partnerPlaceInput}
              onChange={(e) => {
                setPartnerPlaceInput(e.target.value);
                setLocationSelected(false);
                setSelectedLocation(null);
                setHasTypedPlace(true);
              }}
              placeholder="Enter place of birth"
              required
            />
  
            {!locationSelected && (locationLoading || locationPredictions.length > 0) && (
              <ul className="predictions" style={{ position: 'relative', zIndex: 1000 }}>
                {locationLoading ? (
                  <li>Finding location...</li>
                ) : (
                  locationPredictions.map((prediction) => (
                    <li
                      key={prediction.place_id}
                      onClick={() => {
                        setPartnerPlaceInput(prediction.name);
                        setLocationSelected(true);
                        setSelectedLocation({
                          lat: prediction.lat,
                          lon: prediction.lon,
                        });
                        setLocationPredictions([]);
                      }}
                    >
                      {prediction.name}
                    </li>
                  ))
                )}
              </ul>
            )}
  
            <button type="submit" className="start-love-chat">Continue</button>
          </form>
        </div>
      </div>
    );
  }   
  return (
    <div className="report-container">
        {/* Top bar */}
        <div className="top-section">
            <div className="top-section-content">
            <Link to="/mini-reports" className="no-underline">
                <i className="ri-arrow-left-line" style={{ fontSize: '1.5rem' }}></i>
            </Link>
            <div className="profile-pic-container">
                <img src="/astro.png" alt="Astrologer" className="profile-pic" />
                <div className="status-dot"></div>
            </div>
            <div className="astrologer-info">
                <h4>Acharya Raghavendra</h4>
                <h5>Your Personalized Report</h5>
            </div>
            </div>
        </div>

        {/* Report content */}
        <div className="report-body">
            <h2 className="report-title">{meta.title}</h2>

            {!isUnlocked ? (
            <div className="unlock-section">
                <p class="report-highlights-desc">It includes insights on:</p>
            
                <ul className="section-list-preview">
                {meta.sections.map((section, i) => (
                    <li key={i}>
                    <i className="ri-star-fill" style={{ marginRight: '6px', color: '#f39c12' }}></i>
                    <span class="section-list-text">{section}</span>
                    </li>
                ))}
                </ul>
                <p class="report-amount-text">This report costs ₹{meta.price}.</p>
                
            
                <button onClick={() => handleReportPurchase(reportType, meta.price)} className="unlock-btn" disabled={loadingPurchase}>
                  {loadingPurchase ? 'Processing...' : '🔓 Unlock My Report'}
                </button>
            </div>
            ) : (
                <div className="section-wrapper">
                {meta.sections.map((section, i) => (
                    <div key={section} className="accordion-section">
                        <button
                        className={`accordion-toggle ${openSection === section ? 'active' : ''}`}
                        onClick={() => fetchSectionContent(section)}
                        >
                        {section}
                        <i className={`ri-arrow-${openSection === section ? 'up' : 'down'}-s-line`}></i>
                        </button>

                        {openSection === section && (
                        <div className="accordion-content">
                            {loadingSection === section && !reportData[section] ? (
                            <p className="section-loading">🔮 Generating...</p>
                            ) : (
                            <ReactMarkdown className="section-text">
                                {reportData[section]}
                            </ReactMarkdown>
                            )}
                        </div>
                        )}
                    </div>
                ))}
              </div>              
            )}
        </div>
    </div>

  )
};
  
export default ReportDetailPage;
  